/**
 * Main App Component
 * Routes between screens and manages global state
 */

import React, { useEffect, useCallback, useState } from 'react';
import { useGameStore } from './store/gameStore';
import { useSRSStore } from './store/srsStore';
import { useTranslation } from './hooks/useTranslation';
import { calculateEraProgress, getLocalizedEraName } from './utils/historiography';
import { initAudio } from './utils/sound';
import { buildShareImage } from './utils/share';

// Screens
import { LandingScreen, BotDifficulty } from './components/screens/LandingScreen';
import { GameScreen } from './components/screens/GameScreen';
import { QuestionScreen } from './components/screens/QuestionScreen';
import { PerspectiveScreen } from './components/screens/PerspectiveScreen';
import { ReviewScreen } from './components/screens/ReviewScreen';
import { TrackerScreen } from './components/screens/TrackerScreen';

import type { Language } from './i18n/config';
import type { SRSRating } from './types/question';

import './styles/variables.css';
import './styles/global.css';

const App: React.FC = () => {
  const { t, changeLanguage, currentLanguage, getGameLabels, getQuestionLabels, getPerspectiveLabels, getReviewLabels, getTrackerLabels } = useTranslation();
  
  // Game store
  const {
    currentScreen,
    setScreen,
    startGame,
    startDemo,
    selectCard,
    submitAnswer,
    continueAfterQuestion,
    completePerspective,
    skipPerspective,
    pass,
    take,
    decrementTimer,
    extendTimer,
    useHint,
    selectOption,
    playerHand,
    botHand,
    playZone,
    deck,
    trump,
    discard,
    xp,
    streak,
    bestStreak,
    correctCount,
    totalQuestions,
    answered,
    isPlayerAttacking,
    pendingCard,
    currentQuestion,
    questionTimeRemaining,
    selectedOption,
    hasAnswered,
    answerCorrect,
    xpGain,
    pendingPerspective,
    matchStartTime,
    getMatchDuration,
    eliminatedOptions,
    hintUsed,
    botDifficulty,
    setBotDifficulty,
    poolResetToast,
    clearPoolResetToast,
  } = useGameStore();

  // SRS store
  const srsStore = useSRSStore();
  const dueCards = srsStore.getDueCards();

  // Initialize audio on first interaction
  useEffect(() => {
    const handleInteraction = () => {
      initAudio();
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };
    document.addEventListener('click', handleInteraction);
    document.addEventListener('touchstart', handleInteraction);
    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };
  }, []);

  // Timer effect
  useEffect(() => {
    if (currentScreen !== 'question' || hasAnswered) return;

    const interval = setInterval(() => {
      decrementTimer();
    }, 1000);

    return () => clearInterval(interval);
  }, [currentScreen, hasAnswered, decrementTimer]);

  // Handle language change
  const handleLanguageChange = useCallback((lang: Language) => {
    changeLanguage(lang);
    document.documentElement.lang = lang === 'lt' ? 'lt' : lang === 'be' ? 'be' : 'en';
  }, [changeLanguage]);

  // Handle game start
  const handleStartGame = useCallback(() => {
    startGame();
  }, [startGame]);

  // Handle demo start
  const handleStartDemo = useCallback(() => {
    startDemo();
  }, [startDemo]);

  // Handle card selection
  const handleCardSelect = useCallback((card: typeof playerHand[0]) => {
    selectCard(card);
  }, [selectCard]);

  // Handle answer submission
  const handleSubmitAnswer = useCallback(() => {
    submitAnswer();
  }, [submitAnswer]);

  // Handle continue after question
  const handleContinue = useCallback(() => {
    continueAfterQuestion();
  }, [continueAfterQuestion]);

  // Handle perspective completion
  const handlePerspectiveConfirm = useCallback((answer: 0 | 1 | 2) => {
    completePerspective(answer);
  }, [completePerspective]);

  // Handle perspective skip
  const handlePerspectiveSkip = useCallback(() => {
    skipPerspective();
  }, [skipPerspective]);

  // Handle SRS rating
  const handleRateQuestion = useCallback((questionId: string, rating: SRSRating) => {
    srsStore.rateCard(questionId, rating);
  }, [srsStore]);

  // Handle hint
  const handleHint = useCallback(() => {
    useHint();
  }, [useHint]);

  // Handle difficulty change
  const handleDifficultyChange = useCallback((difficulty: BotDifficulty) => {
    setBotDifficulty(difficulty);
  }, [setBotDifficulty]);

  // Calculate era progress
  const eraProgress = calculateEraProgress(answered);
  const localizedEras = eraProgress.map(era => ({
    ...era,
    name: getLocalizedEraName(era, currentLanguage),
  }));

  // Get match duration - memoized to avoid recalculation on every render
  const matchDuration = React.useMemo(() => {
    return matchStartTime ? getMatchDuration() : '0:00';
  }, [matchStartTime, getMatchDuration]);

  // Podkidnoy Durak: first to empty their hand wins. The deck must be drained first.
  const playerWon = playerHand.length === 0 && deck.length === 0;

  // Auto-dismiss pool-reset toast after 3 s
  useEffect(() => {
    if (!poolResetToast) return;
    const t = setTimeout(clearPoolResetToast, 3000);
    return () => clearTimeout(t);
  }, [poolResetToast, clearPoolResetToast]);

  // Share toast state
  const [showShareToast, setShowShareToast] = useState(false);

  // Handle share: canvas image → Web Share API → clipboard fallback
  const handleShare = useCallback(async () => {
    const text = `Playing History – Durak\nXP: ${xp} | Correct: ${correctCount}/${totalQuestions} | Best Streak: 🔥×${bestStreak}\nplaying-history.eu`;

    // Try Web Share API with canvas image (supported on mobile browsers)
    if (navigator.share) {
      try {
        const blob = await buildShareImage(xp, correctCount, totalQuestions, bestStreak, playerWon);
        const shareData: ShareData = { title: t('app.shareTitle'), text };
        if (blob && navigator.canShare?.({ files: [new File([blob], 'result.png', { type: 'image/png' })] })) {
          shareData.files = [new File([blob], 'result.png', { type: 'image/png' })];
        }
        await navigator.share(shareData);
        return;
      } catch {
        // User cancelled or share failed — fall through to clipboard
      }
    }

    // Clipboard fallback
    try {
      await navigator.clipboard.writeText(text);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 2500);
    } catch {
      console.error('Failed to copy to clipboard');
    }
  }, [xp, correctCount, totalQuestions, bestStreak, playerWon, t]);

  // Render current screen
  const renderScreen = () => {
    switch (currentScreen) {
      case 'landing':
        return (
          <LandingScreen
            currentLang={currentLanguage}
            onLanguageChange={handleLanguageChange}
            onStartGame={handleStartGame}
            onStartDemo={handleStartDemo}
            botDifficulty={botDifficulty}
            onDifficultyChange={handleDifficultyChange}
          />
        );

      case 'game':
        return (
          <GameScreen
            playerHand={playerHand}
            botHandCount={botHand.length}
            playZone={playZone}
            deckCount={deck.length}
            discardCount={discard.length}
            trump={trump}
            xp={xp}
            streak={streak}
            isPlayerAttacking={isPlayerAttacking}
            selectedCard={pendingCard}
            onCardSelect={handleCardSelect}
            onPass={pass}
            onTake={take}
            onTracker={() => setScreen('tracker')}
            labels={getGameLabels()}
          />
        );

      case 'question':
        if (!currentQuestion || !pendingCard) return null;
        return (
          <QuestionScreen
            question={currentQuestion}
            card={pendingCard}
            difficulty={currentQuestion.difficulty}
            timeLimit={currentQuestion.difficulty === 'easy' ? 30 : currentQuestion.difficulty === 'medium' ? 45 : 60}
            timeRemaining={questionTimeRemaining}
            selectedOption={selectedOption}
            hasAnswered={hasAnswered}
            isCorrect={answerCorrect}
            xpGain={xpGain}
            eliminatedOptions={eliminatedOptions}
            hintUsed={hintUsed}
            streak={streak}
            onSelectOption={selectOption}
            onSubmit={handleSubmitAnswer}
            onContinue={handleContinue}
            onHint={handleHint}
            onExtendTime={extendTimer}
            labels={getQuestionLabels()}
          />
        );

      case 'perspective':
        if (!pendingPerspective) return null;
        return (
          <PerspectiveScreen
            perspective={pendingPerspective}
            onConfirm={handlePerspectiveConfirm}
            onSkip={handlePerspectiveSkip}
            labels={getPerspectiveLabels()}
          />
        );

      case 'review':
        return (
          <ReviewScreen
            xp={xp}
            correctCount={correctCount}
            totalQuestions={totalQuestions}
            bestStreak={bestStreak}
            matchTime={matchDuration}
            playerWon={playerWon}
            answeredQuestions={answered}
            dueCards={dueCards}
            onRateQuestion={handleRateQuestion}
            onPlayAgain={handleStartGame}
            onShare={handleShare}
            onTracker={() => setScreen('tracker')}
            labels={getReviewLabels()}
          />
        );

      case 'tracker':
        return (
          <TrackerScreen
            eras={localizedEras}
            onBackToGame={() => setScreen('game')}
            onBackToReview={() => setScreen('review')}
            labels={getTrackerLabels()}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="app">
      <a href="#main-content" className="skip-link">
        {t('app.skipToContent')}
      </a>
      
      <main id="main-content" role="main">
        {renderScreen()}
      </main>

      {/* Share toast notification */}
      {showShareToast && (
        <div 
          className="share-toast" 
          role="status" 
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--surface)',
            color: 'var(--text)',
            padding: '12px 24px',
            borderRadius: '8px',
            border: '1px solid var(--neutral)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 1000,
            fontSize: '0.9rem',
          }}
        >
          {t('app.shareCopied')}
        </div>
      )}

      {/* Pool-reset toast — shown when all questions at a difficulty tier have been seen */}
      {poolResetToast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: '60px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--surface)',
            color: 'var(--text)',
            padding: '12px 24px',
            borderRadius: '8px',
            border: '1px solid var(--neutral)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 1000,
            fontSize: '0.9rem',
            whiteSpace: 'nowrap',
          }}
        >
          {t('app.poolReset')}
        </div>
      )}

      {/* Demo navigation for development */}
      {import.meta.env.DEV && (
        <details className="demo-nav-details" style={{ position: 'fixed', bottom: '10px', right: '10px', zIndex: 999 }}>
          <summary style={{ 
            cursor: 'pointer', 
            fontSize: '0.7rem', 
            color: '#6e6e8e',
            background: 'var(--surface)',
            padding: '4px 8px',
            borderRadius: '4px',
            border: '1px solid #334',
            userSelect: 'none'
          }}>
            DEV
          </summary>
          <nav className="demo-nav" aria-label="Development navigation" style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            marginTop: '4px',
            background: 'var(--surface)',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #334',
          }}>
            <button onClick={() => setScreen('landing')}>🏠 Start</button>
            <button onClick={() => setScreen('game')}>🃏 Game</button>
            <button onClick={() => setScreen('question')}>❓ Question</button>
            <button onClick={() => setScreen('perspective')}>⚖️ Perspective</button>
            <button onClick={() => setScreen('review')}>📊 Review</button>
            <button onClick={() => setScreen('tracker')}>📜 Tracker</button>
          </nav>
        </details>
      )}
    </div>
  );
};

export default App;
